<template>
  <div class="subCategory" :class="$i18n.locale">
    <!-- Reload Layer -->
    <div v-if="loading" class="reload-layer">
      <div class="spinner">
        <div class="spinner-border spinner-border-xxl" role="status">
          <span class="sr-only">Loading...</span>
        </div>
        <!-- <p class="loading-text">{{ $t("loading") }}</p> -->
      </div>
    </div>

    <!-- Navigation -->
    <div
      class="navigation d-none d-lg-flex justify-content-start align-items-center"
    >
      <nav aria-label="breadcrumb">
        <ol class="breadcrumb">
          <li class="breadcrumb-item">
            <router-link to="/">{{ $t("items.home") }}</router-link>
          </li>
          <li class="breadcrumb-item">
            <router-link
              v-if="hasCategoryTitle"
              :to="`/categories/${categoryId}`"
            >
              {{ categoryTitle }}
            </router-link>
            <span v-else>{{ $t("home.noDataTill") }}</span>
          </li>
        </ol>
      </nav>
    </div>

    <!-- Cover Image -->
    <SingleImageCover v-if="hasCoverImage" :image="cover_image_path" />

    <!-- Categories Section -->
    <section>
      <div class="container">
        <div class="tabs-holder">
          <div class="tabs-content VueSlickCarousel-holder">
            <div class="row align-items-center">
              <!-- All Categories Button -->
              <div class="col-md-1 col-sm-12 mb-2">
                <div
                  @click="selectDefaultTab"
                  class="category-slide mb-5 allCat cursor-pointer"
                  :class="{ active: isAllCategoriesActive }"
                >
                  <img
                    src="@/assets/images/book-open-reader-solid.svg"
                    alt="all-categories"
                    width="75"
                    height="75"
                  />
                  <p class="small-text mb-0 mt-2 text-center">
                    {{ $t("home.All") }}
                  </p>
                </div>
              </div>
              <!-- Categories Slider -->
              <div class="col-md-11 col-sm-12 mb-2">
                <carousel
                  :per-page="slidesPerPage"
                  :navigation-enabled="true"
                  :pagination-enabled="false"
                >
                  <slide
                    v-for="(category, index) in subCategories"
                    :key="category.id || index"
                    @click.native="selectTab(category)"
                    class="category-slide d-flex justify-content-center align-items-center flex-column cursor-pointer"
                    :class="{
                      active: currentActiveCategory === category.title,
                      [`brand-${category.title}`]: true,
                    }"
                  >
                    <img
                      :src="category.icon_image_path"
                      alt="category_image"
                      class="categoryImage"
                    />
                    <p
                      class="small-text text-center"
                      v-if="mobile && category.title.length > 12"
                    >
                      {{ category.title.slice(0, 11) + "..." }}
                    </p>
                    <p class="small-text text-center" v-else>
                      {{ category.title }}
                    </p>
                  </slide>
                </carousel>
              </div>
            </div>

            <!-- Content for Selected Tab -->
            <div v-if="isAllCategoriesActive">
              <b-row v-if="loading">
                <b-col v-for="x in 10" :key="x" lg="3" sm="6" class="mb-2">
                  <b-skeleton-img></b-skeleton-img>
                  <b-card>
                    <b-skeleton
                      animation="fade"
                      width="60%"
                      class="border-none"
                    ></b-skeleton>
                    <b-skeleton
                      animation="fade"
                      width="85%"
                      class="border-none"
                    ></b-skeleton>
                  </b-card>
                </b-col>
              </b-row>
              <b-row
                v-else-if="hasAllSubCategories"
                align-h="center"
                align-v="center"
              >
                <b-col
                  v-for="category in finalAllSubTogether"
                  :key="category.id"
                  lg="3"
                  sm="6"
                  class="custum-padding mb-3 p-0"
                  :title="category.title"
                >
                  <router-link :to="`/categories/${category.id}/variants`">
                    <OtherCategoryCard
                      :card="{ type: category.title }"
                      :image="category.image_path"
                    />
                  </router-link>
                </b-col>
              </b-row>
              <h3 v-else class="my-2">{{ $t("home.noDataTill") }}</h3>
            </div>

            <!-- Category-Specific Items -->
            <div v-else>
              <b-row
                v-if="hasActiveCategoryChildren && !loading"
                align-h="center"
                align-v="center"
              >
                <b-col
                  v-for="cat in activeCategoryChildren"
                  :key="cat.id"
                  lg="3"
                  sm="6"
                  class="custum-padding mb-3 p-0"
                >
                  <router-link :to="`/categories/${cat.id}/variants`">
                    <OtherCategoryCard
                      :card="{ type: cat.title }"
                      :image="cat.image_path"
                      class="homecategories"
                    />
                  </router-link>
                </b-col>
              </b-row>
              <h3 v-else class="my-2">
                {{ $t("home.noData") }}
                {{ activeCategory ? activeCategory.title : "" }}
                {{ $t("home.tilNow") }}
              </h3>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import OtherCategoryCard from "@/components/global/OtherCategoryCard.vue";
import SingleImageCover from "@/components/pages/home/SingleImageCover.vue";
import { Carousel, Slide } from "vue-carousel";
import categories from "@/services/categories";

export default {
  name: "SubCategoryPage",
  components: {
    OtherCategoryCard,
    SingleImageCover,
    Carousel,
    Slide,
  },
  data() {
    return {
      id: this.$route.params.slug,
      myActiveAtt: "All",
      currentActiveCategory: "All",
      loading: false,
      subCategories: [],
      finalAllSubTogether: [],
      cover_image_path: null,
      categoryTitle: null,
      categoryId: null,
      activeCategoryChildren: [],
      activeCategory: null,
      mobile: false,
      slidesPerPage: 5,
      searchWord: null,
    };
  },
  computed: {
    hasCategoryTitle() {
      return !!this.categoryTitle;
    },
    hasCoverImage() {
      return !!this.cover_image_path;
    },
    isAllCategoriesActive() {
      return this.myActiveAtt === "All";
    },
    hasAllSubCategories() {
      return this.finalAllSubTogether.length > 0;
    },
    hasActiveCategoryChildren() {
      return this.activeCategoryChildren.length > 0;
    },
    currentSlug() {
      return this.$route.params.slug || null;
    },
    currentBrand() {
      return this.$route.query.brand || null;
    },
  },
  methods: {
    adjustSlidesPerPage() {
      const width = window.innerWidth;
      if (width >= 1200) this.slidesPerPage = 5;
      else if (width >= 992) this.slidesPerPage = 4;
      else if (width >= 768) this.slidesPerPage = 3;
      else if (width >= 576) this.slidesPerPage = 2;
      else this.slidesPerPage = 1;
    },
    checkScreen() {
      this.mobile = window.innerWidth <= 1199;
    },
    async getSubCategories() {
      this.loading = true;
      try {
        const data = { parent_id: this.id, keyword: this.searchWord };
        const resp = await categories.getSubCategories(data);
        this.subCategories = resp.data.items || [];
        this.finalAllSubTogether = this.subCategories
          .flatMap((category) => category.all_children || [])
          .filter(Boolean);
        this.categoryTitle =
          resp.data.items[0]?.parent_category?.title ||
          sessionStorage.getItem("parentTitle") ||
          "";
        if (this.categoryTitle) document.title = this.categoryTitle;
      } catch (error) {
        this.subCategories = [];
        this.finalAllSubTogether = [];
        console.error("Error fetching subcategories:", error);
      } finally {
        this.loading = false;
      }
    },
    async getAllSubCategories() {
      this.loading = true;
      try {
        const resp = await categories.getAllSubCategories(this.id);
        console.log("All subcategories:", resp);
      } catch (error) {
        console.error("Error fetching all subcategories:", error);
      } finally {
        this.loading = false;
      }
    },
    async getCover() {
      this.loading = true;
      try {
        const res = await categories.getSingleProductDetails(this.id);
        this.categoryTitle = res.data.items.title || this.categoryTitle;
        this.categoryId = res.data.items.id;
        this.cover_image_path = res.data.items.cover_image_path;
        sessionStorage.setItem("parentTitle", res.data.items.title);
      } catch (error) {
        console.error("Error fetching cover:", error);
      } finally {
        this.loading = false;
      }
    },
    selectDefaultTab() {
      this.myActiveAtt = "All";
      this.currentActiveCategory = "All";
      this.activeCategoryChildren = [];
      this.activeCategory = null;
      this.$router.push({ path: this.$route.path, query: {} });
      this.scrollToTabs();
    },
    selectTab(category) {
      this.myActiveAtt = category.title.trim().replace(/\s/g, "");
      this.currentActiveCategory = category.title;
      this.activeCategory = category;
      this.activeCategoryChildren = category.all_children || [];
      this.$router.push({
        path: this.$route.path,
        query: { brand: this.myActiveAtt },
      });
      this.scrollToTabs();
    },
    scrollToTabs() {
      const tabsHolder = document.querySelector(".VueSlickCarousel-holder");
      if (tabsHolder) {
        const tabsHeight = tabsHolder.offsetTop - 220;
        window.scrollTo({ top: tabsHeight, behavior: "smooth" });
      }
    },
    initializeTabFromQuery() {
      const brand = this.currentBrand;
      if (brand) {
        const targetCategory = this.subCategories.find(
          (category) => category.title.trim().replace(/\s/g, "") === brand
        );
        if (targetCategory) {
          this.selectTab(targetCategory);
        } else {
          console.warn("No matching category found for brand:", brand);
          this.selectDefaultTab();
        }
      } else {
        this.selectDefaultTab();
      }
    },
  },
  async mounted() {
    this.adjustSlidesPerPage();
    this.checkScreen();
    window.addEventListener("resize", this.adjustSlidesPerPage);
    window.addEventListener("resize", this.checkScreen);
    await Promise.all([
      this.getSubCategories(),
      this.getAllSubCategories(),
      this.getCover(),
    ]);
    this.initializeTabFromQuery();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.adjustSlidesPerPage);
    window.removeEventListener("resize", this.checkScreen);
  },
  metaInfo() {
    return {
      title: this.categoryTitle || document.title || "",
      titleTemplate: "%s",
      htmlAttrs: { lang: localStorage.getItem("lang") || "en", amp: true },
    };
  },
  watch: {
    currentSlug(newSlug, oldSlug) {
      if (newSlug !== oldSlug && newSlug) {
        this.id = newSlug;
        this.getSubCategories();
        this.getAllSubCategories();
        this.getCover();
        this.initializeTabFromQuery();
      }
    },
    currentBrand(newBrand, oldBrand) {
      if (newBrand !== oldBrand) {
        this.initializeTabFromQuery();
      }
    },
  },
};
</script>

<style scoped>
.reload-layer {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}
.spinner {
  text-align: center;
  color: #fff;
}
.loading-text {
  margin-top: 10px;
  font-size: 1.2rem;
}
.reload-layer {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}
.spinner {
  text-align: center;
  color: #fff;
}
.loading-text {
  margin-top: 10px;
  font-size: 1.2rem;
}
</style>

<style lang="scss" scoped>
.cover {
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
}

.cover-data {
  background: rgba(0, 0, 0, 0.35);
}

.select {
  border: none;
  outline: none;
  box-shadow: none;
}

form {
  .xt-blog-form {
    margin-top: 50px;
  }

  .input-group {
    display: block !important;
  }

  .input-group-btn {
    position: absolute;
    right: 5%;
    top: -12%;
    bottom: 0;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;

    button {
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }
}

input {
  &:hover,
  &:focus {
    box-shadow: none;
  }
}

div:empty {
  display: none !important;
  opacity: 0;
}

.custum-padding {
  .category-card {
    margin-right: 7px;
  }
}

.navigation {
  background-color: #f3f4f6;
}

.categoryImage {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  margin-bottom: 10px !important;
  background: #000;
  padding: 2px;
}

.small-text {
  font-size: 16px;
}

.breadcrumb-item a {
  font-weight: normal !important;
}

.active {
  border-radius: 50%;
  img {
    padding: 2px;
    border: 3px solid $main-color; /* Example active styling */
    box-shadow: 0px 0px 2px 2px $main-color;
  }
}
.active.allCat {
  img {
    border-radius: 5px !important;
  }
}
</style>
